import { Chord } from 'tonal';
import {
  MAX_FRET,
  STANDARD_TUNING,
  chromaOf,
  midiAt,
  openStringMidi,
  type Position,
  type Tuning,
} from './fretboard';

/**
 * CAGED and triad shape generation (topics C6, I1). CAGED shapes come in
 * major, minor and dominant-7 forms in standard tuning; close-voiced triads
 * work on any three adjacent strings. Each CAGED shape has a triad core, the
 * close triad on three adjacent strings inside it (research/03: "triads are
 * the cores of the CAGED shapes"), which is what C6 teaches first.
 */

export type CagedShapeName = 'C' | 'A' | 'G' | 'E' | 'D';
export type CagedQuality = 'major' | 'minor' | 'dominant7';
export type Degree = 'R' | '3' | 'b3' | '5' | 'b7';

export const CAGED_QUALITIES: readonly CagedQuality[] = ['major', 'minor', 'dominant7'];

export interface ShapeNote extends Position {
  readonly degree: Degree;
}

export interface CagedShape {
  readonly shape: CagedShapeName;
  readonly quality: CagedQuality;
  readonly root: string;
  /** Fret of the root on the shape's root string. */
  readonly rootFret: number;
  readonly rootString: number;
  /** Low-to-high: the lowest-pitched string first. */
  readonly notes: readonly ShapeNote[];
}

interface Template {
  rootString: number;
  notes: readonly { string: number; offset: number; degree: Degree }[];
}

/**
 * CAGED templates, named after the open chord each one is (C x32010, Am
 * x02210, G7 320001 …). Offsets are relative to the root fret on `rootString`.
 * The minor C and G shapes put the 5th on string 1 (C) or string 2 (G) where
 * the major 3rd was, because the b3 would sit a 4-fret stretch below the nut.
 */
                                                                           
          
        
                    
              
                                              
                                               
                                               
                                               
                                               
        
      
        
                    
              
                                              
                                              
                                              
                                              
                                              
        
      
        
                    
              
                                              
                                               
                                               
                                               
                                               
                                              
        
      
        
                    
              
                                              
                                              
                                              
                                              
                                              
                                              
        
      
        
                    
              
                                              
                                              
                                              
                                              
        
      
    
          
        
                    
              
                                              
                                                
                                               
                                               
                                              
        
      
        
                    
              
                                              
                                              
                                              
                                               
                                              
        
      
        
                    
              
                                              
                                                
                                               
                                               
                                              
                                              
        
      
        
                    
              
                                              
                                              
                                              
                                               
                                              
                                              
        
      
        
                    
              
                                              
                                              
                                              
                                               
        
      
    
              
        
                    
              
                                              
                                               
                                               
                                               
                                               
        
      
        
                    
              
                                              
                                              
                                               
                                              
                                              
        
      
        
                    
              
                                              
                                               
                                               
                                               
                                               
                                                
        
      
        
                    
              
                                              
                                              
                                               
                                              
                                              
                                              
        
      
        
                    
              
                                              
                                              
                                               
                                              
        
      
    
  

/** The CAGED chain order, ascending the neck. */
                                                                                

export interface CagedOptions {
  /** Defaults to `major`. */
  readonly quality?: CagedQuality;
  /** Place the shape at the lowest position whose frets are all ≥ this. Default 0. */
  readonly minFret?: number;
}

/** The shape that follows `shape` up the neck (D wraps round to C). */
                                                                       
                                                                                              
 

/** The shape that precedes `shape` down the neck (C wraps round to D). */
                                                                           
                                                                                              
 

/**
 * The CAGED shape for `root`, placed at the lowest neck position whose frets
 * are all ≥ `minFret` (default 0). Standard tuning only.
 */
                           
                        
               
                          
               
                                          
                                      
                                    
                                                              
                                                                        
                                                                  
                                                        
                                                       
 

                       
                        
                        
               
                   
               
                                      
                                     
                     
                              
                     
      
                                                           
                                                                                            
   
                                                                             
 

/** Lowest and highest fretted positions of a shape (open strings count as 0). */
                                                                        
               
             
   
                                           
                                                              
 

/**
 * The five shapes of one chord, chained up the neck from the lowest position
 * (e.g. C major: C → A → G → E → D).
 */
                                                                                               
                                                                   
                                                                      
 

export type TriadQuality = 'major' | 'minor';
export type Inversion = 0 | 1 | 2;

export interface TriadVoicing {
  readonly root: string;
  readonly quality: TriadQuality;
  readonly inversion: Inversion;
  /** Low-to-high: the lowest-pitched string first. */
  readonly notes: readonly ShapeNote[];
}

                                                                
                         
                          
  

/**
 * Close-voiced triads on three adjacent strings (`lowString` and the two above
 * it), within a 4-fret stretch and inside `[0, maxFret]`. The inversion is set
 * by the bass note: root position R-3-5, 1st 3-5-R, 2nd 5-R-3.
 */
                              
               
                        
                    
                       
                                                                        
                   
                                                
                                     
                                          
                                                                                             
                                                 
                                                 
                                                                       
                                         
                                                          
                                                            

                                                              
                             
                                                                                    
                                                          
               
    

                                    
                                                           
                                                         
                                                     
                                                       
                                                         
                                   
                                                                           
                                                            
                                                            
                                                            
                                                                     
                                                            
                     
               
                  
                    
                  
                                                                    
                                                                    
                                                                    
            
           
       
     
   
                                                                                  
 

export interface TriadCore extends TriadVoicing {
  readonly shape: CagedShapeName;
  /** Lowest-pitched string of the three-string set, e.g. 3 for strings 3–1. */
  readonly lowString: number;
}

/** The triad inside a three-string slice of a shape, or null if the slice isn't a close triad. */
                                                                                            
                                                                      
                                          
    
                                                      
                                     
                                         
                                                                            
                                            
                                                         
                                                         
                                                                                 
                                                                                
 

/**
 * Every close-voiced triad on three adjacent strings inside the shape, top
 * string set first. A dominant-7 shape's cores are those of the major shape at
 * the same position (7 = major triad + b7).
 */
                                                        
                                                                                                   
                                                                          
                              
                                      
                                            
                             
   
             
 

/**
 * The triad core taught first for a shape: the highest string set that holds
 * a complete close triad (strings 3–1 for C, A, E and D; 4–2 for G, and for
 * the minor C shape; 5–3 for the minor G shape).
 */
                                                     
                                
                                                                                
              
 
